import { serialize } from 'object-to-formdata';
import { useEffect, useRef, useState } from 'react';
import { IUserUpdateInputModel } from 'inputModels';
import {
  IRefRegisterAddressData,
  IRefRegisterPersonalData,
  IRefRegisterProfileData,
  IRefRegisterServiceData,
  IRefRegisterUrlData,
  IRefRegisterTypeServiceData,
} from 'entities';
import { IAddressViewModel } from 'viewModels';
import { NavHeader, MenuOptionLeftCompany, Button } from '../../components';
import { RegisterAddress } from '../../components/RegisterAddress';
import { RegisterPersonalData } from '../../components/RegisterPersonalData';
import { RegisterProfile } from '../../components/RegisterProfile';
import { RegisterService } from '../../components/RegisterService';
import { RegisterUrl } from '../../components/RegisterUrl';
import { useApp, useAuth, useRegisterForm } from '../../context';
import { Container, Content } from './styles';
import { CompanyController } from '../../controller/CompanyController';
import { useAlert } from '../../hooks/useAlert';
import { unMask } from '../../utils/removeMask';
import { useSpecialties } from '../../context/specialties';
import { useCorrespondences } from '../../context/correspondences';
import { RegisterTypeServices } from '../../components/RegisterTypeServices';
import { InviteFriend } from '../../components/InviteFriend';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { TutorialType } from '../../types/enums';
import { prepareExternalContactsForSubmit } from '../../utils/validateExternalContact';

export function LegalPersonAccount() {
  const alert = useAlert();
  const app = useApp();
  const { user } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [hasPageForSearch, setHasPageForSearch] = useState<boolean>();
  const [runningTutorial, setRunningTutorial] = useState(false);
  const {
    hasNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
    setSpecialtiesSelectedByUser,
  } = useSpecialties();
  const { setCorrespondencesSelectedByUser } = useCorrespondences();
  const personalDataRef = useRef<IRefRegisterPersonalData>(null);
  const addressDataRef = useRef<IRefRegisterAddressData>(null);
  const serviceDataRef = useRef<IRefRegisterServiceData>(null);
  const serviceTypeDataRef = useRef<IRefRegisterTypeServiceData>(null);
  const profileDataRef = useRef<IRefRegisterProfileData>(null);
  const urlDataRef = useRef<IRefRegisterUrlData>(null);

  const {
    setFormSubmited,
    setActiveForm,
    isLoading,
    setFirstName,
    setLastName,
    setTradingName,
    setAddress,
    setIsServiceProvider,
    setPresentationLetter,
    setPictureProfile,
    setExternalContacts,
    setLinkFacebook,
    setLinkInstagram,
    setLinkLinkedIn,
    setLinkSite,
    setLinkTwitter,
    setUrl,
  } = useRegisterForm();

  const steps = [
    {
      target: '#start-item',
      content:
        'A página de Perfil é onde você pode atualizar suas informações pessoais, de endereço, de serviços, entre outros.',
      disableBeacon: true,
    },
    {
      target: '#document-input',
      content:
        'Digite seu documento de identificação, caso seja pessoa física, digite seu CPF, caso seja pessoa jurídica, digite seu CNPJ.',
    },
    {
      target: '#name-input',
      content:
        'Após inserir seu documento, seu nome deve aparecer automaticamente, caso não apareça, digite seu nome completo.',
    },
    {
      target: '#cep',
      content: 'Insira seu CEP',
      delay: 1000,
    },
    {
      target: '#address',
      content:
        'Automaticamente seu endereço será buscado com base no seu CEP, caso falte informações, complete manualmente.',
    },
    {
      target: '#expand-register-service',
      content:
        'Você pode clicar para expandir esta aba e selecionar se deseja prestar ou não serviços, se for prestar serviços, selecione suas comarcas de atuação.',
    },
    {
      target: '#select-specialties',
      content: 'Selecione as suas especialidades, você pode selecionar mais de uma especialidade.',
    },
    {
      target: '#select-correspondences',
      content:
        'Selecione as correspondências que você deseja prestar serviços, você pode selecionar mais de uma correspondência.',
    },
    {
      target: '#contacts',
      content:
        'Aqui você pode adicionar contatos externos, como telefone, email, whatsapp, entre outros. e configurar suas redes sociais',
    },
    {
      target: '#profile-url',
      content: 'Aqui você pode adicionar mudar a URL de referencia para o seu perfil.',
    },
    {
      target: '#invite-friends',
      content: 'Aqui você pode convidar outras pessoas para a plataforma.',
    },
    {
      target: '#save-button',
      content: 'Clique aqui para salvar as alterações.',
    },
  ];

  const handleUpdateProfile = async () => {
    setFormSubmited(true);
    const personalData = await personalDataRef.current?.handleUpdatePersonalData();
    const addressData = addressDataRef.current?.handleUpdateAddressData();
    const serviceData = serviceDataRef.current?.handleUpdateServiceData();
    const profileData = await profileDataRef.current?.handleUpdateProfileData();
    const urlData = await urlDataRef.current?.handleUpdateUrlData();
    const serviceTypeData = serviceTypeDataRef.current?.handleUpdateTypeServiceData();

    if (
      !personalData ||
      !addressData ||
      !serviceData ||
      !profileData ||
      !urlData ||
      !serviceTypeData
    ) {
      alert.show({
        message: 'Falha ao tentar buscar os dados do formulário, tente novamente!',
        type: 'error',
      });
      return;
    }
    const hasError =
      personalData.hasError ||
      addressData.hasError ||
      serviceData.hasError ||
      urlData.hasError ||
      serviceTypeData.hasError;
    if (hasError) {
      if (personalData.hasError) {
        personalDataRef.current?.handleFocusOnError();
        return;
      }
      if (addressData.hasError) {
        addressDataRef.current?.handleFocusOnError();
        setActiveForm('registerAddress');
        return;
      }
      if (serviceData.hasError) {
        serviceDataRef.current?.handleFocusOnError();
        setActiveForm('registerService');
        return;
      }
      if (serviceTypeData.hasError) {
        serviceTypeDataRef.current?.handleFocusOnError();
        setActiveForm('registerTypeService');
        return;
      }
      return;
    }

    const update: IUserUpdateInputModel = {
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      tradingName: addressData.tradingName,
      address: addressData.address,
      isServiceProvider: serviceData.isProviderService,
      //districtsId: serviceData.districtsSelected.map((district) => district.id),
      specialties: serviceTypeData.specialtiesSelected.map((specialty) => specialty.id),
      correspondences: serviceTypeData.correspondencesSelected.map(
        (correspondence) => correspondence.id,
      ),
      profilePictureId: profileData.pictureProfile?.id,
      externalContacts: prepareExternalContactsForSubmit(profileData.externalContacts),
      presentation: profileData.presentationLetter,
      linkFacebook: profileData.linkFacebook,
      linkInstagram: profileData.linkInstagram,
      linkLinkedIn: profileData.linkLinkedIn,
      linkSite: profileData.linkSite,
      linkTwitter: profileData.linkTwitter,
      url: urlData.url,
    };
    const formData = serialize(update, { indices: true });
    if (profileData.pictureProfile?.file)
      formData.append('profilePicture', profileData.pictureProfile?.file);

    setIsSending(true);
    CompanyController.update(formData)
      .then(() => {
        setFormSubmited(false);
        alert.show({ message: 'Mudanças aplicadas com sucesso!', type: 'success' });
        setFirstName(update.firstName ?? '');
        setLastName(update.lastName ?? '');
        setTradingName(update.tradingName ?? '');
        setAddress(update.address ?? ({} as IAddressViewModel));
        setIsServiceProvider(!!update.isServiceProvider);
        //setDistrictsSelectedsByUser(serviceData.districtsSelected);
        setSpecialtiesSelectedByUser(serviceTypeData.specialtiesSelected);
        setCorrespondencesSelectedByUser(serviceTypeData.correspondencesSelected);
        setPresentationLetter(update.presentation ?? '');
        setPictureProfile(profileData.pictureProfile);
        setExternalContacts(profileData.externalContacts);
        setLinkFacebook(update.linkFacebook ?? '');
        setLinkInstagram(update.linkInstagram ?? '');
        setLinkLinkedIn(update.linkLinkedIn ?? '');
        setLinkSite(update.linkSite ?? '');
        setLinkTwitter(update.linkTwitter ?? '');
        setUrl(update.url ?? '');
        setActiveForm('');
      })
      .catch(() => {
        alert.show({
          message: 'Falha ao tentar atualizar o perfil, tente novamente!',
          type: 'error',
        });
      })
      .finally(() => setIsSending(false));
  };

  useEffect(() => {
    app.setLoading(isLoading || isSending);
  }, [app, isLoading, isSending]);
  useEffect(() => {
    setHasPageForSearch(
      hasNextPageSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialty,
    );
  }, [
    hasNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
  ]);

  const handleSteps = (target: string) => {
    if (target === '#cep') {
      addressDataRef.current?.handleTourDefaultValues();
    }
  };

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.LEGAL_PERSON_ACCOUNT)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <JoyrideTour
        type={TutorialType.LEGAL_PERSON_ACCOUNT}
        steps={steps}
        run={runningTutorial}
        onStepChange={handleSteps}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <Container>
        <MenuOptionLeftCompany />
        <Content>
          <div className="page-title">
            <TitlePage
              title="Perfil"
              onRequestOpen={() => {
                serviceTypeDataRef.current?.handleTourDefaultValues();
                setRunningTutorial(true);
              }}
              id="start-item"
            />
          </div>
          <RegisterPersonalData ref={personalDataRef} />
          <RegisterAddress ref={addressDataRef} />
          <RegisterService ref={serviceDataRef} />
          <RegisterTypeServices ref={serviceTypeDataRef} />
          <RegisterProfile ref={profileDataRef} />
          <RegisterUrl ref={urlDataRef} />
          <InviteFriend />
          <Button
            id="save-button"
            className="buttonSave"
            text="Salvar"
            dimensions={{ width: 150, height: 30 }}
            onClick={handleUpdateProfile}
            disabled={isLoading || isSending || hasPageForSearch}
          />
        </Content>
      </Container>
    </>
  );
}
